import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { getPdfAssets, addLogo, drawCompanyBlock, applyWatermarkAllPages } from "../lib/pdf";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { FileText, Download } from "lucide-react";
import { exportToExcel } from "../lib/excel";
import { API } from "../lib/api";
import { sortByName } from "../lib/sortByName";
import { SearchableSelect } from "../components/SearchableSelect";
import { DatePicker } from "../components/DatePicker";

const Relatorios = () => {
  const [diarias, setDiarias] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [prestadores, setPrestadores] = useState([]);
  const [funcoes, setFuncoes] = useState([]);
  const [relatorioINSS, setRelatorioINSS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingINSS, setLoadingINSS] = useState(false);
  const [activeTab, setActiveTab] = useState("a-pagar");
  const [filtroINSS, setFiltroINSS] = useState({
    ano: new Date().getFullYear(),
    mes: new Date().getMonth() + 1
  });
  
  // Prestadores selecionados para o relatório INSS (usando objeto ao invés de array)
  const [prestadoresSelecionados, setPrestadoresSelecionados] = useState({});
  
  // Filtros de data para os relatórios A Pagar e A Cobrar
  const [filtroData, setFiltroData] = useState({
    dataInicio: '',
    dataFim: ''
  });
  
  // Filtro de cliente para o relatório A Cobrar
  const [filtroCliente, setFiltroCliente] = useState('');
  
  // Filtro de prestador para o relatório A Pagar
  const [filtroPrestador, setFiltroPrestador] = useState('todos');

  const fetchData = async () => {
    try {
      const [diariasRes, clientesRes, prestadoresRes, funcoesRes] = await Promise.all([
        axios.get(`${API}/diarias`),
        axios.get(`${API}/clientes`),
        axios.get(`${API}/prestadores`),
        axios.get(`${API}/funcoes`)
      ]);
      
      setDiarias(diariasRes.data);
      setClientes(clientesRes.data);
      setPrestadores(prestadoresRes.data);
      // Ordenar funções alfabeticamente por nome
      const funcoesOrdenadas = funcoesRes.data.sort(sortByName);
      setFuncoes(funcoesOrdenadas);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatorioINSS = async () => {
    setLoadingINSS(true);
    try {
      const response = await axios.get(`${API}/relatorio-inss`, {
        params: {
          ano: filtroINSS.ano,
          mes: filtroINSS.mes
        }
      });
      setRelatorioINSS(response.data);
      // Limpar seleção ao gerar novo relatório
      setPrestadoresSelecionados({});
    } catch (error) {
      toast.error("Erro ao carregar relatório INSS");
      setRelatorioINSS(null);
    } finally {
      setLoadingINSS(false);
    }
  };

  useEffect(() => {
    const carregar = async () => { await fetchData(); };
    carregar();
  }, []);

  useEffect(() => {
    if (activeTab === "inss") {
      const carregar = async () => { await fetchRelatorioINSS(); };
      carregar();
    }
  }, [activeTab, filtroINSS]);

  const getClienteNome = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    return cliente ? cliente.nome : 'Cliente não encontrado';
  };

  const getPrestadorNome = (prestadorId) => {
    const prestador = prestadores.find(p => p.id === prestadorId);
    return prestador ? prestador.nome : 'Prestador não encontrado';
  };

  const getFuncaoNome = (funcaoId) => {
    const funcao = funcoes.find(f => f.id === funcaoId);
    return funcao ? funcao.nome : 'Função não encontrada';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [ano, mes, dia] = dateString.split('-');
    return `${dia}/${mes}/${ano}`;
  };

  // Relatório A Pagar (agrupado por prestador)
  const relatorioAPagar = () => {
    const grouped = {};
    
    // Filtrar diárias por data e prestador se os filtros estão definidos e excluir diárias já pagas
    const diariasFiltradas = diarias.filter(diaria => {
      // Excluir diárias já pagas (não devem aparecer no relatório A Pagar)
      if (diaria.pago === true) {
        return false;
      }
      
      // Filtro por prestador
      if (filtroPrestador && filtroPrestador !== 'todos' && diaria.prestador_id !== filtroPrestador) {
        return false;
      }
      
      // Filtro por data
      if (filtroData.dataInicio || filtroData.dataFim) {
        const dataDiaria = diaria.data; // Manter como string YYYY-MM-DD
        const dataInicio = filtroData.dataInicio;
        const dataFim = filtroData.dataFim;
        
        // Comparar as datas como strings no formato YYYY-MM-DD
        if (dataInicio && dataDiaria < dataInicio) return false;
        if (dataFim && dataDiaria > dataFim) return false;
      }
      
      return true;
    });
    
    diariasFiltradas.forEach(diaria => {
      const prestadorId = diaria.prestador_id;
      if (!grouped[prestadorId]) {
        grouped[prestadorId] = {
          prestador: getPrestadorNome(prestadorId),
          diarias: [],
          total: 0
        };
      }
      grouped[prestadorId].diarias.push(diaria);
      grouped[prestadorId].total += diaria.valor_pagar;
    });

    return Object.values(grouped);
  };

  // Relatório de Fechamento (agrupado por cliente)
  const relatorioACobrar = () => {
    const grouped = {};
    
    // Filtrar diárias por data e cliente se os filtros estão definidos
    const diariasFiltradas = diarias.filter(diaria => {
      // Filtro de data
      if (filtroData.dataInicio || filtroData.dataFim) {
        const dataDiaria = diaria.data; // Manter como string YYYY-MM-DD
        const dataInicio = filtroData.dataInicio;
        const dataFim = filtroData.dataFim;
        
        // Comparar as datas como strings no formato YYYY-MM-DD
        if (dataInicio && dataDiaria < dataInicio) return false;
        if (dataFim && dataDiaria > dataFim) return false;
      }
      
      // Filtro de cliente
      if (filtroCliente && filtroCliente !== "todos" && diaria.cliente_id !== filtroCliente) {
        return false;
      }
      
      return true;
    });
    
    diariasFiltradas.forEach(diaria => {
      const clienteId = diaria.cliente_id;
      if (!grouped[clienteId]) {
        grouped[clienteId] = {
          cliente: getClienteNome(clienteId),
          diarias: [],
          total: 0
        };
      }
      grouped[clienteId].diarias.push(diaria);
      grouped[clienteId].total += diaria.valor_cobrar;
    });

    // Ordenar diárias por data crescente dentro de cada grupo de cliente
    Object.values(grouped).forEach(group => {
      group.diarias.sort((a, b) => new Date(a.data) - new Date(b.data));
    });

    return Object.values(grouped);
  };

  const exportExcel = (data, filename) => {
    if (data.length === 0) {
      toast.error("Nenhum dado para exportar");
      return;
    }

    let rows = [];

    if (activeTab === "a-pagar") {
      rows = data.flatMap(group =>
        group.diarias.map(diaria => ({
          'Prestador': group.prestador,
          'Data': formatDate(diaria.data),
          'Cliente': getClienteNome(diaria.cliente_id),
          'Função': getFuncaoNome(diaria.funcao_id),
          'Valor Pagar': Number((diaria.valor_pagar || 0).toFixed(2)),
          'Override': diaria.tem_override ? 'Sim' : 'Não',
          'Observações': diaria.observacoes || '',
        }))
      );
    } else {
      let totalHorasExtras = 0;
      let totalAcrescimo = 0;

      rows = data.flatMap(group =>
        group.diarias.map(diaria => {
          const temHoraExtra = diaria.horas_extras && diaria.horas_extras > 0;
          const jornada = diaria.jornada_padrao_horas ? `${diaria.jornada_padrao_horas}h` : '-';
          const horasTrab = diaria.horas_trabalhadas ? `${diaria.horas_trabalhadas}h` : '-';
          const horasExtras = temHoraExtra ? `${diaria.horas_extras}h` : '-';

          let valorBase = diaria.valor_cobrar;
          let acrescimo = 0;
          if (temHoraExtra && diaria.valor_hora_normal_cliente && diaria.valor_hora_extra_cliente) {
            const horasNormais = diaria.jornada_padrao_horas || 0;
            valorBase = horasNormais * diaria.valor_hora_normal_cliente;
            acrescimo = diaria.horas_extras * diaria.valor_hora_extra_cliente;
            totalHorasExtras += diaria.horas_extras;
            totalAcrescimo += acrescimo;
          }

          const obs = temHoraExtra
            ? `Hora extra: ${diaria.horas_extras}h (+10%). ${diaria.observacoes || ''}`
            : (diaria.observacoes || '');

          return {
            'Cliente': group.cliente,
            'Data': formatDate(diaria.data),
            'Prestador': getPrestadorNome(diaria.prestador_id),
            'Função': getFuncaoNome(diaria.funcao_id),
            'Jornada': jornada,
            'Horas Trabalhadas': horasTrab,
            'Horas Extras': horasExtras,
            'Valor Base': Number(valorBase.toFixed(2)),
            'Acréscimo HE': Number(acrescimo.toFixed(2)),
            'Valor Final': Number((diaria.valor_cobrar || 0).toFixed(2)),
            'Observações': obs,
          };
        })
      );

      if (totalHorasExtras > 0) {
        rows.push({ 'Cliente': '' });
        rows.push({ 'Cliente': 'RESUMO HORAS EXTRAS' });
        rows.push({ 'Cliente': 'Total de horas extras no período:', 'Data': `${totalHorasExtras.toFixed(2)}h` });
        rows.push({ 'Cliente': 'Total de acréscimo por horas extras:', 'Data': `R$ ${totalAcrescimo.toFixed(2)}` });
      }
    }

    exportToExcel(rows, filename, activeTab === "a-pagar" ? 'A Pagar' : 'A Cobrar');
    toast.success("Relatório exportado com sucesso!");
  };


  // Funções para gerenciar seleção de prestadores no relatório INSS
  // Função simplificada - apenas toggle do ID específico
  const handleCheckboxChange = (prestadorId) => {
    setPrestadoresSelecionados(prev => {
      const newState = { ...prev };
      if (newState[prestadorId]) {
        delete newState[prestadorId];
      } else {
        newState[prestadorId] = true;
      }
      return newState;
    });
  };

  const selecionarTodosPrestadores = () => {
    if (relatorioINSS && relatorioINSS.prestadores) {
      const todos = {};
      relatorioINSS.prestadores.forEach(p => {
        todos[p.prestador.id] = true;
      });
      setPrestadoresSelecionados(todos);
    }
  };

  const deselecionarTodosPrestadores = () => {
    setPrestadoresSelecionados({});
  };

  // Filtrar relatório INSS apenas com prestadores selecionados
  const getRelatorioINSSFiltrado = () => {
    if (!relatorioINSS) return null;
    
    return {
      ...relatorioINSS,
      prestadores: relatorioINSS.prestadores.filter(p => 
        prestadoresSelecionados[p.prestador.id] === true
      )
    };
  };

  const exportRelatorioINSS = () => {
    const relatorioFiltrado = getRelatorioINSSFiltrado();
    
    if (!relatorioFiltrado || relatorioFiltrado.prestadores.length === 0) {
      toast.error("Nenhum prestador selecionado para exportar");
      return;
    }

    const rows = relatorioFiltrado.prestadores.map(item => {
      const p = item.prestador;
      return {
        'Nome': p.nome || '',
        'CPF': p.cpf_cnpj || '',
        'Data Nascimento': p.data_nascimento ? formatDate(p.data_nascimento) : '',
        'RG': p.rg || '',
        'Data Expedição RG': p.rg_data_expedicao ? formatDate(p.rg_data_expedicao) : '',
        'PIS': p.pis || '',
        'CNPJ MEI': p.cnpj_mei || '',
        'Pai': p.filiacao_pai || '',
        'Mãe': p.filiacao_mae || '',
        'CEP': p.cep || '',
        'Endereço': p.endereco || '',
        'Cidade': p.cidade || '',
        'Estado': p.estado || '',
        'Funções Exercidas': item.funcoes_exercidas.join('; '),
        'Valor Total': Number((item.valor_total || 0).toFixed(2)),
        'Qtd Diárias': item.diarias_count,
      };
    });

    exportToExcel(
      rows,
      `relatorio-inss-${filtroINSS.mes.toString().padStart(2, '0')}-${filtroINSS.ano}`,
      'INSS'
    );
    toast.success("Relatório INSS exportado com sucesso!");
  };

  const exportRelatorioINSSPDF = async () => {
    const relatorioFiltrado = getRelatorioINSSFiltrado();
    
    if (!relatorioFiltrado || relatorioFiltrado.prestadores.length === 0) {
      toast.error("Nenhum prestador selecionado para exportar");
      return;
    }

    try {
      const { logoDataUrl, empresa } = await getPdfAssets();
      const doc = new jsPDF();
      
      // Logo + dados da empresa
      addLogo(doc, logoDataUrl, 10, 10, 38, 19);
      drawCompanyBlock(doc, empresa, 52, 14);
      
      // Cabeçalho principal dourado/bronze
      doc.setFillColor(155, 118, 23); // Dourado/bronze
      doc.rect(10, 35, 190, 10, 'F');
      
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(255, 255, 255); // Texto branco
      doc.text(`Período: ${filtroINSS.mes.toString().padStart(2, '0')}/${filtroINSS.ano}`, 15, 42);
      doc.text(`Total de prestadores: ${relatorioFiltrado.prestadores.length}`, 150, 42);
      
      let yPosition = 52;
      
      // Resetar cor do texto para preto
      doc.setTextColor(0, 0, 0);
      
      // Dados dos prestadores em formato de blocos verticais (sem colunas)
      relatorioFiltrado.prestadores.forEach((item, index) => {
        const p = item.prestador;
        
        // Verificar se precisa de nova página
        if (yPosition > 240) {
          doc.addPage();
          
          // Logo + dados da empresa na nova página
          addLogo(doc, logoDataUrl, 10, 10, 38, 19);
          drawCompanyBlock(doc, empresa, 52, 14);
          
          // Cabeçalho dourado/bronze na nova página
          doc.setFillColor(155, 118, 23);
          doc.rect(10, 35, 190, 10, 'F');
          doc.setFontSize(11);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(255, 255, 255);
          doc.text(`Período: ${filtroINSS.mes.toString().padStart(2, '0')}/${filtroINSS.ano}`, 15, 42);
          doc.text(`Total de prestadores: ${relatorioINSS.prestadores.length}`, 150, 42);
          
          yPosition = 52;
          doc.setTextColor(0, 0, 0);
        }
        
        // Nome do prestador (linha principal em negrito)
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text(p.nome || 'Nome não informado', 15, yPosition);
        
        // Valor e diárias alinhados à direita
        const valorText = `${formatCurrency(item.valor_total)} (${item.diarias_count} diárias)`;
        const valorWidth = doc.getTextWidth(valorText);
        doc.text(valorText, 195 - valorWidth, yPosition);
        
        yPosition += 7;
        
        // Dados em formato vertical (uma informação por linha)
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        
        // CPF
        doc.text(`CPF: ${p.cpf_cnpj || 'Não informado'}`, 15, yPosition);
        yPosition += 5;
        
        // Data de Nascimento
        if (p.data_nascimento) {
          doc.text(`Data de Nascimento: ${formatDate(p.data_nascimento)}`, 15, yPosition);
          yPosition += 5;
        }
        
        // RG e Data de Expedição
        doc.text(`RG: ${p.rg || 'Não informado'}`, 15, yPosition);
        if (p.rg_data_expedicao) {
          doc.text(`Data Expedição: ${formatDate(p.rg_data_expedicao)}`, 80, yPosition);
        }
        yPosition += 5;
        
        // PIS
        doc.text(`PIS: ${p.pis || 'Não informado'}`, 15, yPosition);
        yPosition += 5;
        
        // CNPJ MEI
        doc.text(`CNPJ MEI: ${p.cnpj_mei || 'Não informado'}`, 15, yPosition);
        yPosition += 5;
        
        // Filiação (Pai)
        if (p.filiacao_pai) {
          doc.text(`Pai: ${p.filiacao_pai}`, 15, yPosition);
          yPosition += 5;
        }
        
        // Filiação (Mãe)
        if (p.filiacao_mae) {
          doc.text(`Mãe: ${p.filiacao_mae}`, 15, yPosition);
          yPosition += 5;
        }
        
        // Endereço completo
        if (p.endereco || p.cidade || p.estado) {
          const enderecoCompleto = `${p.endereco || ''}${p.cidade ? ', ' + p.cidade : ''}${p.estado ? ' - ' + p.estado : ''}`;
          doc.text(`Endereço: ${enderecoCompleto}`, 15, yPosition);
          yPosition += 5;
        }
        
        // CEP
        if (p.cep) {
          doc.text(`CEP: ${p.cep}`, 15, yPosition);
          yPosition += 5;
        }
        
        // Funções exercidas
        if (item.funcoes_exercidas && item.funcoes_exercidas.length > 0) {
          doc.text(`Funções: ${item.funcoes_exercidas.join(', ')}`, 15, yPosition);
          yPosition += 5;
        }
        
        // Linha de separação entre blocos
        yPosition += 3;
        doc.setLineWidth(0.3);
        doc.setDrawColor(200, 200, 200);
        doc.line(10, yPosition, 200, yPosition);
        yPosition += 8;
      });
      
      // Total geral no final
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 50;
      }
      
      doc.setFillColor(230, 230, 230); // Cinza claro
      doc.rect(10, yPosition - 2, 190, 15, 'F');
      
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      const totalGeral = relatorioFiltrado.prestadores.reduce((sum, item) => sum + item.valor_total, 0);
      doc.text(`TOTAL GERAL: ${formatCurrency(totalGeral)}`, 15, yPosition + 6);
      doc.text(`TOTAL DE PRESTADORES: ${relatorioFiltrado.prestadores.length}`, 15, yPosition + 12);
      
      // Salvar PDF usando Blob para maior compatibilidade
      applyWatermarkAllPages(doc, logoDataUrl);
      const filename = `relatorio-inss-${filtroINSS.mes.toString().padStart(2, '0')}-${filtroINSS.ano}`;
      const pdfBlob = doc.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
        URL.revokeObjectURL(url);
      }, 100);
      
      toast.success("Relatório INSS PDF exportado com sucesso!");
      
    } catch (error) {
      console.error('Erro ao gerar PDF INSS:', error);
      toast.error("Erro ao gerar PDF INSS: " + error.message);
    }
  };

  const exportPDF = async (data, filename) => {
    if (data.length === 0) {
      toast.error("Nenhum dado para exportar");
      return;
    }

    try {
      const { logoDataUrl, empresa } = await getPdfAssets();
      const doc = new jsPDF();
      
      // Variáveis para totalizadores de horas extras
      let totalHorasExtrasGeral = 0;
      let totalAcrescimoGeral = 0;
      
      // Logo + dados da empresa (empresa alinhada à direita)
      addLogo(doc, logoDataUrl, 10, 8, 30, 16);
      drawCompanyBlock(doc, empresa, doc.internal.pageSize.getWidth() - 12, 12, { align: 'right', nameSize: 11 });
      
      // Cabeçalho
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      const title = activeTab === "a-pagar" 
        ? "Relatório A Pagar por Prestador" 
        : "Relatório de Fechamento";
      doc.text(title, 50, 20);
      
      // Data do relatório
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const now = new Date();
      doc.text(`Gerado em: ${now.toLocaleDateString('pt-BR')} às ${now.toLocaleTimeString('pt-BR')}`, 50, 28);
      
      // Período filtrado (se houver)
      if (filtroData.dataInicio || filtroData.dataFim) {
        const periodo = `Período: ${filtroData.dataInicio ? formatDate(filtroData.dataInicio) : 'Início'} até ${filtroData.dataFim ? formatDate(filtroData.dataFim) : 'Fim'}`;
        doc.text(periodo, 50, 34);
      }
      
      let yPosition = 50;
      
      // Gerar relatório
      data.forEach((group, groupIndex) => {
        // Verificar se precisa de nova página
        if (yPosition > 240) {
          doc.addPage();
          yPosition = 20;
        }
        
        // Nome do prestador/cliente
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        const groupName = activeTab === "a-pagar" ? group.prestador : group.cliente;
        doc.text(groupName, 20, yPosition);
        
        // Total do grupo
        doc.text(`Total: ${formatCurrency(group.total)}`, 150, yPosition);
        yPosition += 8;
        
        // Cabeçalho da tabela
        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        doc.text('Data', 20, yPosition);
        doc.text(activeTab === "a-pagar" ? 'Cliente' : 'Prestador', 45, yPosition);
        doc.text('Função', 95, yPosition);
        doc.text('Valor', 155, yPosition);
        yPosition += 5;
        
        // Linha horizontal
        doc.line(20, yPosition, 180, yPosition);
        yPosition += 3;
        
        // Diárias do grupo
        doc.setFont(undefined, 'normal');
        group.diarias.forEach(diaria => {
          if (yPosition > 265) {
            doc.addPage();
            yPosition = 20;
          }
          
          const temHoraExtra = diaria.horas_extras && diaria.horas_extras > 0;
          const valor = activeTab === "a-pagar" ? diaria.valor_pagar : diaria.valor_cobrar;
          
          // Linha principal da diária
          doc.text(formatDate(diaria.data), 20, yPosition);
          doc.text((activeTab === "a-pagar" ? getClienteNome(diaria.cliente_id) : getPrestadorNome(diaria.prestador_id)).substring(0, 25), 45, yPosition);
          doc.text(getFuncaoNome(diaria.funcao_id).substring(0, 25), 95, yPosition);
          doc.text(formatCurrency(valor), 155, yPosition);
          
          // Se for relatório de fechamento (a-cobrar) e tiver hora extra, mostrar detalhes
          if (activeTab === "a-cobrar" && temHoraExtra) {
            yPosition += 4;
            
            // Calcular valor base e acréscimo
            const horasNormais = diaria.jornada_padrao_horas || 8;
            const valorHoraNormal = diaria.valor_hora_normal_cliente || (valor / (diaria.horas_trabalhadas || horasNormais));
            const valorBase = horasNormais * valorHoraNormal;
            const acrescimo = diaria.horas_extras * (diaria.valor_hora_extra_cliente || (valorHoraNormal * 1.1));
            
            // Acumular totais
            totalHorasExtrasGeral += diaria.horas_extras;
            totalAcrescimoGeral += acrescimo;
            
            // Linha de detalhes com indentação
            doc.setFontSize(7);
            doc.setTextColor(100, 100, 100);
            const detalheHE = `↳ Hora extra: ${diaria.horas_extras.toFixed(1)}h (+10%) | Jornada: ${horasNormais}h | Base: ${formatCurrency(valorBase)} + Acréscimo: ${formatCurrency(acrescimo)}`;
            doc.text(detalheHE, 25, yPosition);
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(8);
          }
          
          yPosition += 5;
        });
        
        yPosition += 5; // Espaço entre grupos
      });
      
      // Total geral
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      
      doc.line(20, yPosition, 180, yPosition);
      yPosition += 8;
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      const totalGeral = data.reduce((sum, group) => sum + group.total, 0);
      doc.text(`TOTAL GERAL: ${formatCurrency(totalGeral)}`, 20, yPosition);
      
      // Se for relatório de fechamento e tiver horas extras, mostrar resumo
      if (activeTab === "a-cobrar" && totalHorasExtrasGeral > 0) {
        yPosition += 12;
        
        // Box de resumo de horas extras
        doc.setFillColor(255, 250, 240); // Cor de fundo amarelo claro
        doc.rect(20, yPosition - 4, 160, 20, 'F');
        doc.setDrawColor(255, 180, 0);
        doc.rect(20, yPosition - 4, 160, 20, 'S');
        
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('RESUMO DE HORAS EXTRAS NO PERÍODO', 25, yPosition + 2);
        
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
        doc.text(`Total de horas extras: ${totalHorasExtrasGeral.toFixed(2)}h`, 25, yPosition + 8);
        doc.text(`Total de acréscimo por horas extras: ${formatCurrency(totalAcrescimoGeral)}`, 25, yPosition + 14);
      }
      
      // Salvar PDF usando Blob para maior compatibilidade
      applyWatermarkAllPages(doc, logoDataUrl);
      const pdfBlob = doc.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
        URL.revokeObjectURL(url);
      }, 100);
      
      toast.success("Relatório PDF exportado com sucesso!");
      
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error("Erro ao gerar PDF: " + error.message);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Carregando...</div>;
  }

  const dadosAPagar = relatorioAPagar();
  const dadosACobrar = relatorioACobrar();
  const totalGeral = activeTab === "a-pagar" 
    ? dadosAPagar.reduce((sum, group) => sum + group.total, 0)
    : dadosACobrar.reduce((sum, group) => sum + group.total, 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Relatórios</h1>
          <p className="text-gray-600 dark:text-gray-300">Relatórios de pagamentos, cobranças e INSS</p>
        </div>
      </div>

      {/* Tabs dos Relatórios */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="a-pagar">A Pagar por Prestador</TabsTrigger>
          <TabsTrigger value="a-cobrar">Relatório de Fechamento</TabsTrigger>
          <TabsTrigger value="inss">Relatório INSS</TabsTrigger>
        </TabsList>

        {/* Filtros para A Pagar */}
        {activeTab === "a-pagar" && (
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="text-lg">Filtros</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div>
                  <Label htmlFor="filtroPrestadorSelect">Prestador</Label>
                  <SearchableSelect
                    options={[
                      { value: 'todos', label: 'Todos os prestadores' },
                      ...prestadores
                        .filter(p => p.status === 'ativo')
                        .sort(sortByName)
                        .map(prestador => ({
                          value: prestador.id,
                          label: prestador.nome
                        }))
                    ]}
                    value={filtroPrestador}
                    onValueChange={setFiltroPrestador}
                    placeholder="Todos os prestadores"
                    searchPlaceholder="Buscar prestador..."
                    emptyText="Nenhum prestador encontrado"
                    className="w-full"
                  />
                </div>
                <div>
                  <Label htmlFor="dataInicio">Data Início</Label>
                  <DatePicker
                    id="dataInicio"
                    value={filtroData.dataInicio}
                    onChange={(value) => setFiltroData({...filtroData, dataInicio: value})}
                    placeholder="Data início"
                  />
                </div>
                <div>
                  <Label htmlFor="dataFim">Data Fim</Label>
                  <DatePicker
                    id="dataFim"
                    value={filtroData.dataFim}
                    onChange={(value) => setFiltroData({...filtroData, dataFim: value})}
                    placeholder="Data fim"
                    minDate={filtroData.dataInicio}
                  />
                </div>
                <div>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setFiltroData({ dataInicio: '', dataFim: '' });
                      setFiltroPrestador('todos');
                    }}
                  >
                    Limpar Filtros
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filtros para Relatório de Fechamento (com filtro de cliente) */}
        {activeTab === "a-cobrar" && (
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="text-lg">Filtros</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div>
                  <Label htmlFor="filtroClienteSelect">Cliente</Label>
                  <SearchableSelect
                    options={[
                      { value: 'todos', label: 'Todos os clientes' },
                      ...clientes.map(cliente => ({
                        value: cliente.id,
                        label: cliente.nome
                      }))
                    ]}
                    value={filtroCliente}
                    onValueChange={setFiltroCliente}
                    placeholder="Todos os clientes"
                    searchPlaceholder="Buscar cliente..."
                    emptyText="Nenhum cliente encontrado"
                    className="w-full"
                  />
                </div>
                <div>
                  <Label htmlFor="dataInicioACobrar">Data Início</Label>
                  <DatePicker
                    id="dataInicioACobrar"
                    value={filtroData.dataInicio}
                    onChange={(value) => setFiltroData({...filtroData, dataInicio: value})}
                    placeholder="Data início"
                  />
                </div>
                <div>
                  <Label htmlFor="dataFimACobrar">Data Fim</Label>
                  <DatePicker
                    id="dataFimACobrar"
                    value={filtroData.dataFim}
                    onChange={(value) => setFiltroData({...filtroData, dataFim: value})}
                    placeholder="Data fim"
                    minDate={filtroData.dataInicio}
                  />
                </div>
                <div>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setFiltroData({ dataInicio: '', dataFim: '' });
                      setFiltroCliente('todos');
                    }}
                  >
                    Limpar Filtros
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <TabsContent value="a-pagar">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Relatório A Pagar por Prestador</CardTitle>
                  <CardDescription>
                    Total Geral: <span className="font-bold text-lg text-red-600 dark:text-red-400">{formatCurrency(totalGeral)}</span>
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => exportExcel(dadosAPagar, 'relatorio-a-pagar')} data-testid="exportar-apagar-excel-btn">
                    <Download className="mr-2 h-4 w-4" />
                    Excel
                  </Button>
                  <Button onClick={() => exportPDF(dadosAPagar, 'relatorio-a-pagar')} variant="outline">
                    <FileText className="mr-2 h-4 w-4" />
                    PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {dadosAPagar.map((group) => (
                <div key={group.prestador} className="mb-6">
                  <div className="flex justify-between items-center mb-3 p-3 bg-gray-50 dark:bg-muted/40 rounded">
                    <h3 className="font-semibold text-lg">{group.prestador}</h3>
                    <span className="font-bold text-red-600 dark:text-red-400">{formatCurrency(group.total)}</span>
                  </div>
                  <div className="ml-4">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead>Cliente</TableHead>
                          <TableHead>Função</TableHead>
                          <TableHead>Valor</TableHead>
                          <TableHead>Override</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.diarias.map((diaria) => (
                          <TableRow key={diaria.id}>
                            <TableCell>{formatDate(diaria.data)}</TableCell>
                            <TableCell>{getClienteNome(diaria.cliente_id)}</TableCell>
                            <TableCell>{getFuncaoNome(diaria.funcao_id)}</TableCell>
                            <TableCell className="font-medium text-red-600 dark:text-red-400">
                              {formatCurrency(diaria.valor_pagar)}
                            </TableCell>
                            <TableCell>
                              {diaria.tem_override ? (
                                <Badge variant="destructive">Sim</Badge>
                              ) : (
                                <Badge variant="outline">Não</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="a-cobrar">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Relatório de Fechamento</CardTitle>
                  <CardDescription>
                    Total Geral: <span className="font-bold text-lg text-green-600 dark:text-green-400">{formatCurrency(totalGeral)}</span>
                    {(() => {
                      // Calcular total de horas extras do período
                      const totalHE = dadosACobrar.reduce((acc, group) => 
                        acc + group.diarias.reduce((sum, d) => sum + (d.horas_extras || 0), 0), 0);
                      if (totalHE > 0) {
                        return <span className="ml-4 text-orange-600 dark:text-orange-400 text-sm">| Total de horas extras: {totalHE.toFixed(1)}h</span>;
                      }
                      return null;
                    })()}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => exportExcel(dadosACobrar, 'relatorio-a-cobrar')} data-testid="exportar-acobrar-excel-btn">
                    <Download className="mr-2 h-4 w-4" />
                    Excel
                  </Button>
                  <Button onClick={() => exportPDF(dadosACobrar, 'relatorio-a-cobrar')} variant="outline">
                    <FileText className="mr-2 h-4 w-4" />
                    PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {dadosACobrar.map((group) => (
                <div key={group.cliente} className="mb-6">
                  <div className="flex justify-between items-center mb-3 p-3 bg-gray-50 dark:bg-muted/40 rounded">
                    <h3 className="font-semibold text-lg">{group.cliente}</h3>
                    <span className="font-bold text-green-600 dark:text-green-400">{formatCurrency(group.total)}</span>
                  </div>
                  <div className="ml-4 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-24 min-w-[96px]">Data</TableHead>
                          <TableHead className="w-40 min-w-[160px]">Prestador</TableHead>
                          <TableHead className="w-28 min-w-[112px]">Função</TableHead>
                          <TableHead className="w-20 min-w-[80px] text-center">Jornada</TableHead>
                          <TableHead className="w-24 min-w-[96px] text-right">Valor</TableHead>
                          <TableHead className="w-32 min-w-[128px]">Hora Extra</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.diarias.map((diaria) => {
                          const temHoraExtra = diaria.horas_extras && diaria.horas_extras > 0;
                          return (
                          <TableRow key={diaria.id} className={temHoraExtra ? 'bg-orange-50 dark:bg-orange-950/40' : ''}>
                            <TableCell className="w-24 min-w-[96px] font-mono text-sm">
                              {formatDate(diaria.data)}
                            </TableCell>
                            <TableCell className="w-40 min-w-[160px]">
                              <div className="truncate pr-2" title={getPrestadorNome(diaria.prestador_id)}>
                                {getPrestadorNome(diaria.prestador_id)}
                              </div>
                            </TableCell>
                            <TableCell className="w-28 min-w-[112px]">
                              <div className="truncate pr-2" title={getFuncaoNome(diaria.funcao_id)}>
                                {getFuncaoNome(diaria.funcao_id)}
                              </div>
                            </TableCell>
                            <TableCell className="w-20 min-w-[80px] text-center text-sm">
                              {diaria.jornada_padrao_horas ? `${diaria.jornada_padrao_horas}h` : '-'}
                            </TableCell>
                            <TableCell className="w-24 min-w-[96px] font-medium text-green-600 dark:text-green-400 text-right">
                              {formatCurrency(diaria.valor_cobrar)}
                            </TableCell>
                            <TableCell className="w-32 min-w-[128px]">
                              {temHoraExtra ? (
                                <div className="text-xs">
                                  <Badge variant="outline" className="bg-orange-100 dark:bg-orange-950/40 text-orange-700 dark:text-orange-400 border-orange-300">
                                    +{diaria.horas_extras.toFixed(1)}h extra
                                  </Badge>
                                  <span className="block text-orange-600 dark:text-orange-400 mt-1">+10% adicional</span>
                                </div>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500 text-xs">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="inss">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Relatório INSS por Prestador</CardTitle>
                  <CardDescription>
                    Dados para pagamento do INSS por período
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={exportRelatorioINSS} 
                    disabled={!relatorioINSS || Object.keys(prestadoresSelecionados).length === 0}
                    data-testid="exportar-inss-excel-btn"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Excel
                  </Button>
                  <Button 
                    onClick={exportRelatorioINSSPDF} 
                    variant="outline" 
                    disabled={!relatorioINSS || Object.keys(prestadoresSelecionados).length === 0}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filtros INSS */}
              <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="ano">Ano</Label>
                    <Input
                      id="ano"
                      type="number"
                      value={filtroINSS.ano}
                      onChange={(e) => setFiltroINSS({...filtroINSS, ano: parseInt(e.target.value)})}
                      min="2020"
                      max="2030"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mes">Mês</Label>
                    <Select value={filtroINSS.mes.toString()} onValueChange={(value) => setFiltroINSS({...filtroINSS, mes: parseInt(value)})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Janeiro</SelectItem>
                        <SelectItem value="2">Fevereiro</SelectItem>
                        <SelectItem value="3">Março</SelectItem>
                        <SelectItem value="4">Abril</SelectItem>
                        <SelectItem value="5">Maio</SelectItem>
                        <SelectItem value="6">Junho</SelectItem>
                        <SelectItem value="7">Julho</SelectItem>
                        <SelectItem value="8">Agosto</SelectItem>
                        <SelectItem value="9">Setembro</SelectItem>
                        <SelectItem value="10">Outubro</SelectItem>
                        <SelectItem value="11">Novembro</SelectItem>
                        <SelectItem value="12">Dezembro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button onClick={fetchRelatorioINSS} disabled={loadingINSS}>
                      {loadingINSS ? 'Carregando...' : 'Gerar Relatório'}
                    </Button>
                  </div>
                </div>
              </div>

              {loadingINSS && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-2 text-gray-600 dark:text-gray-300">Gerando relatório INSS...</p>
                </div>
              )}

              {relatorioINSS && relatorioINSS.prestadores.length > 0 && (
                <div className="space-y-4">
                  {/* Header com resumo e botões de seleção */}
                  <div className="bg-green-50 dark:bg-green-950/40 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <h3 className="font-semibold text-green-800 dark:text-green-300">Período: {relatorioINSS.periodo}</h3>
                        <p className="text-green-700 dark:text-green-400">
                          Total de prestadores: {relatorioINSS.total_prestadores} | 
                          Selecionados: {Object.keys(prestadoresSelecionados).length}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={selecionarTodosPrestadores}
                        >
                          Selecionar Todos
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={deselecionarTodosPrestadores}
                        >
                          Desselecionar Todos
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Lista de prestadores com checkbox */}
                  {relatorioINSS.prestadores.map((item) => {
                    const isSelected = prestadoresSelecionados[item.prestador.id] === true;
                    return (
                    <div 
                      key={`inss-prestador-${item.prestador.id}`} 
                      className={`border rounded-lg p-4 transition-all ${
                        isSelected
                          ? 'border-green-500 bg-green-50 dark:bg-green-950/40' 
                          : 'border-gray-200 dark:border-border bg-white dark:bg-card'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Checkbox */}
                        <div className="pt-1">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleCheckboxChange(item.prestador.id)}
                            className="w-5 h-5 text-green-600 dark:text-green-400 rounded focus:ring-green-500 cursor-pointer"
                          />
                        </div>
                        
                        {/* Conteúdo do prestador */}
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="text-lg font-semibold">{item.prestador.nome}</h3>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-sm text-gray-600 dark:text-gray-300">
                                <div><strong>CPF:</strong> {item.prestador.cpf_cnpj}</div>
                                <div><strong>RG:</strong> {item.prestador.rg || '-'}</div>
                                <div><strong>PIS:</strong> {item.prestador.pis || '-'}</div>
                                <div><strong>CNPJ MEI:</strong> {item.prestador.cnpj_mei || '-'}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-green-600 dark:text-green-400">{formatCurrency(item.valor_total)}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-300">{item.diarias_count} diárias</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
                        <div>
                          <p><strong>Pai:</strong> {item.prestador.filiacao_pai || '-'}</p>
                          <p><strong>Mãe:</strong> {item.prestador.filiacao_mae || '-'}</p>
                          <p><strong>Data Exp. RG:</strong> {item.prestador.rg_data_expedicao ? formatDate(item.prestador.rg_data_expedicao) : '-'}</p>
                        </div>
                        <div>
                          <p><strong>Endereço:</strong> {item.prestador.endereco || '-'}</p>
                          <p><strong>CEP:</strong> {item.prestador.cep || '-'}</p>
                          <p><strong>Cidade/Estado:</strong> {item.prestador.cidade || '-'}/{item.prestador.estado || '-'}</p>
                        </div>
                      </div>

                      <div className="border-t pt-2">
                        <p className="text-sm"><strong>Funções exercidas:</strong> {item.funcoes_exercidas.join(', ')}</p>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}

              {relatorioINSS && relatorioINSS.prestadores.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>Nenhum prestador encontrado para o período selecionado.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Relatorios;
